import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
import { ErrorBoundary } from '@atlassian/jira-error-boundary';
import { ReportErrors } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIssueAttachments } from '@atlassian/jira-issue-attachments-base';
import {
	isAttachmentNumberApproachLimit,
	isAttachmentNumberExceedLimit,
} from '@atlassian/jira-issue-attachments-limit';
import { useIssueKey } from '@atlassian/jira-issue-context-service';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import AttachmentsContent from '../content';
import DeleteAllAttachmentsDialog from '../delete-all-dialog';
import AttachmentsHeading from '../heading';
import ExceedWarning from './exceed-warning';

export const Attachments = () => {
	const issueKey = useIssueKey();
	const [
		{
			error,
			value: { totalCount },
		},
		{ refreshAttachments },
	] = useIssueAttachments(issueKey);
	const showAttachmentPanel = totalCount > 0;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (!ff('remove-issueattachmentpanel-viewed-event_w282l')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			const action = 'viewed';
			const actionSubject = 'issueAttachmentPanel';
			const attributes = {
				showAttachmentPanel,
				totalAttachmentCount: totalCount,
				approachLimit: isAttachmentNumberApproachLimit(totalCount),
				exceedLimit: isAttachmentNumberExceedLimit(totalCount),
			};
			fireTrackAnalytics(
				createAnalyticsEvent({ action, actionSubject }),
				`${actionSubject} ${action}`,
				attributes,
			);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);
	}

	if (!showAttachmentPanel) {
		return null;
	}

	return (
		<PanelWrapper
			/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
			data-testid="issue.views.issue-base.content.attachment.panel"
		>
			<AttachmentsHeading totalCount={totalCount} refreshAttachments={refreshAttachments} />
			<ExceedWarning totalCount={totalCount} />
			<AttachmentsContent
				attachmentError={error}
				totalCount={totalCount}
				refreshAttachments={refreshAttachments}
			/>
			<DeleteAllAttachmentsDialog />
		</PanelWrapper>
	);
};

const AttachmentsWithMonitoring = () => (
	<ErrorBoundary id="issue.base.content.attachments-panel">
		<ReportErrors
			id="base.content.attachments-panel"
			/* eslint-disable-next-line jira/js/package-name-enforcer-for-error-analytics */
			packageName="jiraIssueView"
			sendToPrivacyUnsafeSplunk
		>
			<Attachments />
		</ReportErrors>
	</ErrorBoundary>
);

export default AttachmentsWithMonitoring;

// Max-width has been added to avoid broken images in the carousel for clients with a huge widescreen
// 1900px was set based on the maximum number of fetched items - 12 items.

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PanelWrapper = styled.div({
	maxWidth: '1900px',
});
